// ─────────────────────────────────────────────────────────────
// SongInfoPanel — native workspace panel content
// The outer shell (header with icon+name, minimize button) is
// provided by PanelPlaceholder. This component renders the
// sub-tab nav and the active view content only.
// ─────────────────────────────────────────────────────────────

import css from "./STPanelOverlay.sass?inline"
import {createElement} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {Lifecycle, Terminator} from "@opendaw/lib-std"
import {StudioService} from "@/service/StudioService.ts"
import {SongInfoView} from "./views/SongInfoView"
import {SongCatalogView} from "./views/SongCatalogView"
import {CategoriesView} from "./views/CategoriesView"
import {TypesView} from "./views/TypesView"
import {STApi} from "./STApi"

// Adopt all utility CSS (.st-section, .st-btn, .st-nav, etc.)
const className = Html.adoptStyleSheet(css, "SongInfoPanel")

type Tab = "info" | "catalog" | "categories" | "types"

const TAB_LABELS: Record<Tab, string> = {
    info:       "Song Info",
    catalog:    "Catalog",
    categories: "Categories",
    types:      "Types",
}

type Construct = {lifecycle: Lifecycle; service: StudioService}

export const SongInfoPanel = ({lifecycle, service}: Construct) => {
    let activeTab: Tab = "info"
    const viewLifecycle: Terminator = lifecycle.own(new Terminator())

    const content: HTMLElement = <div className="st-content"/>
    const navEl: HTMLElement   = <div className="st-nav"/>

    // ── Render active tab content ─────────────────────────────

    const renderContent = () => {
        viewLifecycle.terminate()
        Html.empty(content)

        const view = (() => {
            switch (activeTab) {
                case "info":       return SongInfoView({lifecycle: viewLifecycle, service})
                case "catalog":    return SongCatalogView({lifecycle: viewLifecycle, service})
                case "categories": return CategoriesView({lifecycle: viewLifecycle})
                case "types":      return TypesView({lifecycle: viewLifecycle})
            }
        })()
        content.appendChild(view as Node)
    }

    // ── Build sub-tab nav ─────────────────────────────────────

    const navBtns: Partial<Record<Tab, HTMLElement>> = {}
    const tabs: Tab[] = ["info", "catalog", "categories", "types"]

    const setTab = (tab: Tab) => {
        activeTab = tab
        for (const [t, btn] of Object.entries(navBtns)) {
            btn?.classList.toggle("active", t === tab)
        }
        STApi.seedFromD1().then(() => renderContent())
    }

    for (const tab of tabs) {
        const btn: HTMLElement = (
            <div className={`st-nav-btn${tab === activeTab ? " active" : ""}`}>
                {TAB_LABELS[tab]}
            </div>
        )
        btn.addEventListener("click", () => setTab(tab))
        navBtns[tab] = btn
        navEl.appendChild(btn)
    }

    // Seed localStorage from D1 then render on mount
    STApi.seedFromD1().then(() => renderContent())

    // Re-render when the project's player type changes (e.g. the user
    // pressed "Switch this project to Song" in Loop Builder Info).
    const onPlayerTypeChanged = () => renderContent()
    document.addEventListener("st:playertype", onPlayerTypeChanged)
    lifecycle.own({terminate: () => document.removeEventListener("st:playertype", onPlayerTypeChanged)})

    return (
        <div className={className}>
            {navEl}
            {content}
        </div>
    )
}
